import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'react-feather';

// Filtro multi-selección genérico de la cola de tickets: botón con contador +
// popover de checkboxes. Lo comparten el filtro de estado y el de agente
// asignado; cualquier filtro nuevo de la barra debería reutilizarlo en vez de
// repetir el popover (cierre por click fuera / Escape incluido).
//
// `seleccionados` son ids en string (vienen de la URL, p. ej. ?estados=1,4);
// las opciones traen ids numéricos, así que se compara como string en ambos
// sentidos.
const TicketsMultiFiltro = ({ etiqueta, opciones, seleccionados, onChange, ariaLabel }) => {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  // Cerrar con click fuera o Escape (mismo patrón que el popover de macros).
  useEffect(() => {
    if (!abierto) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierto]);

  const toggleOpcion = (id) => {
    const idStr = String(id);
    onChange(
      seleccionados.includes(idStr)
        ? seleccionados.filter((s) => s !== idStr)
        : [...seleccionados, idStr],
    );
  };

  return (
    <div className="tickets-filtro" ref={ref}>
      <button
        type="button"
        className={`tickets-btn tickets-btn--secondary tickets-filtro__btn${
          seleccionados.length ? ' is-active' : ''
        }`}
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="true"
      >
        {etiqueta}
        {seleccionados.length > 0 && (
          <span className="tickets-filtro__count">{seleccionados.length}</span>
        )}
        <ChevronDown size={14} aria-hidden="true" />
      </button>

      {abierto && (
        <div className="tickets-filtro__pop" role="group" aria-label={ariaLabel || etiqueta}>
          {opciones.map((o) => (
            <label key={o.id} className="tickets-filtro__opcion">
              <input
                type="checkbox"
                checked={seleccionados.includes(String(o.id))}
                onChange={() => toggleOpcion(o.id)}
              />
              {o.label}
            </label>
          ))}
          {seleccionados.length > 0 && (
            <button type="button" className="tickets-filtro__limpiar" onClick={() => onChange([])}>
              Limpiar filtro
            </button>
          )}
        </div>
      )}
    </div>
  );
};

TicketsMultiFiltro.propTypes = {
  etiqueta: PropTypes.string.isRequired,
  opciones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
  seleccionados: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

TicketsMultiFiltro.defaultProps = {
  opciones: [],
  seleccionados: [],
  ariaLabel: '',
};

export default TicketsMultiFiltro;
